/**
 * Auth initializer — sets up Supabase onAuthStateChange listener.
 *
 * Called once at app startup (main.tsx). Listens for session
 * changes (login, logout, token refresh) and syncs them into
 * the Zustand auth store and the api-client auth header.
 *
 * If Supabase env vars are not set, silently skips initialization
 * (allows the app to run in mock mode during development/testing).
 */

import { getDashboardEnv } from './env';
import { useAuthStore } from './stores/auth-store';
import { hydrateSettingsFromServer } from './stores/settings-store';

let initialized = false;

export async function initAuth(): Promise<void> {
  if (initialized) return;
  initialized = true;

  const { supabaseUrl, supabaseAnonKey } = getDashboardEnv();

  // Skip if Supabase is not configured (mock mode)
  if (!supabaseUrl || !supabaseAnonKey) {
    useAuthStore.getState().setInitialized();
    return;
  }

  try {
    const { getSupabaseClient } = await import('./supabase');
    const supabase = getSupabaseClient();
    const { syncSession, setInitialized } = useAuthStore.getState();

    // Check for existing session (e.g., page refresh with persisted token)
    const { data: { session } } = await supabase.auth.getSession();
    syncSession(session);
    setInitialized();

    // Hydrate settings using the authenticated doctor ID
    if (session) {
      const doctorId = (session.user.user_metadata?.['doctor_id'] as string) ?? session.user.id;
      void hydrateSettingsFromServer(doctorId);
    }

    // Listen for future changes (sign-in, sign-out, token refresh)
    supabase.auth.onAuthStateChange((_event, session) => {
      useAuthStore.getState().syncSession(session);
      if (session) {
        const doctorId = (session.user.user_metadata?.['doctor_id'] as string) ?? session.user.id;
        void hydrateSettingsFromServer(doctorId);
      }
    });
  } catch {
    // If Supabase init fails, mark as initialized so app doesn't hang
    useAuthStore.getState().setInitialized();
  }
}

/** Reset for testing. */
export function resetAuthInit(): void {
  initialized = false;
}
